import { Button, IconClose } from '@odekoteam/doppio';
import { FORM_ERROR } from 'final-form';
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Input,
  Label,
  Textarea,
} from 'theme-ui';
import { useClient } from 'urql';

import { CollectionAuditLogPane } from '@/components/AuditLog/CollectionAuditLogPane';
import CollectionItemsTable, {
  CollectionItem,
} from '@/components/Collection/CollectionItemsTable';
import CreativeAsset from '@/components/Collection/CreativeAsset';
import DeleteCollectionModal from '@/components/Collection/DeleteCollectionModal';
import { ErrorLabel } from '@/components/Collection/Error';
import { LoadingBox } from '@/components/Collection/LoadingBox';
import { UnsavedChangesModal } from '@/components/forms/UnsavedChangesModal';
import { required } from '@/components/forms/utils/Validators';
import useModal from '@/components/useModal';
import {
  useCreateCollectionPromoSetupMutation,
  useGetCollectionSetupForEditQuery,
  useGetWarehousesQuery,
} from '@/graphql/types/generated';
import { useCanDeleteCollection } from '@/hooks/featureflipper';
import { useAllCollectionItems } from '@/hooks/useAllCollectionItems';
import { ErrorActions } from '@/state/errors';
import { showToast, ToastAlertType } from '@/state/toast';
import { isDevelopment } from '@/utils/environment';
import { uploadImage } from '@/utils/uploadImage';

const DESCRIPTION_WORD_LIMIT = 35;

const countWords = (value: string | undefined) =>
  value ? value.trim().split(/\s+/).filter(Boolean).length : 0;

const validateDescription = (value: string) => {
  if (!value) return 'Required';
  if (countWords(value) > DESCRIPTION_WORD_LIMIT) {
    return `Description must be ${DESCRIPTION_WORD_LIMIT} words or fewer`;
  }
  return undefined;
};

type CollectionFormData = {
  name: string;
  slug: string;
  itemIds: string[];
  description: string;
  warehouseIds: string[];
  mobileHeroImage: string;
  desktopHeroImage: string;
};

export default function CollectionPromoPage() {
  const client = useClient();
  const auditModal = useModal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [saving, setSaving] = useState(false);
  const [itemIds, setItemIds] = useState<string[]>([]);
  const isEditMode = !!id;
  const [isDirty, setIsDirty] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const canDeleteCollection = useCanDeleteCollection();
  const { toggle, visible } = useModal();
  const pendingFilesRef = useRef<Map<string, File>>(new Map());
  const [, setPendingFilesVersion] = useState(0);

  const handleFileSelect = useCallback(
    (fieldName: string, file: File | null) => {
      if (file) {
        pendingFilesRef.current.set(fieldName, file);
      } else {
        pendingFilesRef.current.delete(fieldName);
      }
      setPendingFilesVersion((v) => v + 1);
    },
    [],
  );

  const [{ data: warehousesData, fetching: warehousesFetching }] =
    useGetWarehousesQuery({ requestPolicy: 'cache-and-network' });
  const [{ data: collectionData, fetching: collectionFetching }] =
    useGetCollectionSetupForEditQuery({
      pause: !isEditMode,
      variables: { collectionId: id! },
      requestPolicy: 'cache-and-network',
    });
  const [{ error: mutationError }, createCollectionPromoSetup] =
    useCreateCollectionPromoSetupMutation();
  const { edges: itemEdges } = useAllCollectionItems(itemIds);

  const { items, itemWarehouseCounts } = useMemo(() => {
    const countMap = new Map<string, number>();
    const mappedItems: CollectionItem[] =
      itemEdges
        .map((edge) => edge?.node)
        .filter(Boolean)
        .map((node) => {
          const names = (
            'warehouseAssociation' in node!
              ? node!.warehouseAssociation
                  ?.map((wa) => wa?.warehouse.name)
                  .filter((n): n is string => !!n) ?? []
              : []
          ) as string[];
          names.forEach((n) => countMap.set(n, (countMap.get(n) ?? 0) + 1));
          return {
            itemId: node!.id,
            name: node!.name,
            brandName: node!.brandName,
            imgUrl: node!.imgUrl ?? null,
            warehouses: names.sort().join(', '),
          };
        }) ?? [];
    return { items: mappedItems, itemWarehouseCounts: countMap };
  }, [itemEdges]);

  useEffect(() => {
    const editItemIds = collectionData?.collectionSetupForEdit?.itemIds ?? [];
    if (editItemIds.length > 0) {
      setItemIds(editItemIds);
    }
  }, [collectionData]);

  useEffect(() => {
    dispatch(ErrorActions.resetErrors());
    if (mutationError) {
      dispatch(ErrorActions.setGraphqlErrors(mutationError));
    }
  }, [mutationError]);

  const initialValues = useMemo<CollectionFormData>(() => {
    const collection = collectionData?.collectionSetupForEdit?.collection;
    const firstPromo =
      collectionData?.collectionSetupForEdit?.promoPageWarehouses?.[0];
    return {
      name: collection?.name ?? '',
      slug: collection?.slug ?? '',
      description: collection?.description ?? '',
      mobileHeroImage: firstPromo?.mobileHeroImage ?? '',
      desktopHeroImage: firstPromo?.desktopHeroImage ?? '',
      itemIds: collectionData?.collectionSetupForEdit?.itemIds ?? [],
      warehouseIds:
        collectionData?.collectionSetupForEdit?.promoPageWarehouses?.map(
          (pw) => pw.warehouse.id,
        ) ?? [],
    };
  }, [collectionData]);

  if (warehousesFetching || (isEditMode && collectionFetching))
    return (
      <Box sx={{ paddingTop: isDevelopment ? 35.5 : 0 }}>
        <LoadingBox />
      </Box>
    );

  const warehouses = (warehousesData || { warehouses: [] }).warehouses.sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  const onSubmit = async (values: CollectionFormData, form: any) => {
    setSaving(true);
    try {
      // Upload pending files before mutation
      const updatedValues = { ...values };
      try {
        // Upload sequentially to avoid urql deduplicating identical in-flight queries
        for (const [fieldName, file] of pendingFilesRef.current.entries()) {
          const key = await uploadImage(client, file);
          form.change(fieldName, key);
          (updatedValues as any)[fieldName] = key;
        }
      } catch (err) {
        return {
          [FORM_ERROR]:
            err instanceof Error ? err.message : 'Image upload failed',
        };
      }

      const { data, error } = await createCollectionPromoSetup({
        input: {
          name: updatedValues.name,
          slug: updatedValues.slug,
          promoName: updatedValues.name,
          itemIds: updatedValues.itemIds,
          description: updatedValues.description,
          warehouseIds: updatedValues.warehouseIds,
          collectionId: isEditMode ? id : undefined,
          mobileHeroImage: updatedValues.mobileHeroImage,
          desktopHeroImage: updatedValues.desktopHeroImage,
        },
      });

      if (error) {
        return { [FORM_ERROR]: error.message };
      }

      if (data?.createCollectionPromoSetup?.errors?.length) {
        const serverErrors: Record<string, string> = {};
        data.createCollectionPromoSetup.errors.forEach(
          (err: { field?: string | null; message: string }) => {
            if (err.field) {
              serverErrors[err.field] = err.message;
            }
          },
        );
        return serverErrors;
      }

      const collectionId = data?.createCollectionPromoSetup?.collectionId;
      if (collectionId) {
        dispatch(
          showToast({
            alertType: ToastAlertType.SUCCESS,
            header: isEditMode
              ? 'Collection successfully updated!'
              : 'Collection successfully created!',
            ...(!isEditMode && {
              action: {
                label: 'Add Collection to Catalog Homepage',
                href: `/collections/add-to-homepage?collectionId=${collectionId}`,
              },
            }),
          }),
        );
        navigate('/collections');
      }

      return undefined;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form<CollectionFormData>
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ form, values, handleSubmit }) => (
        <>
          <FormSpy
            subscription={{ dirty: true }}
            onChange={(state) => {
              if (isDirty !== state.dirty) {
                setIsDirty(state.dirty ?? false);
              }
            }}
          />
          <Flex
            sx={{
              inset: 0,
              position: 'fixed',
              overflow: 'hidden',
              flexDirection: 'column',
              paddingTop: isDevelopment ? 35.5 : 0,
            }}
          >
            <Flex
              id="navigation"
              sx={{
                px: 6,
                height: 80,
                flexShrink: 0,
                alignItems: 'center',
                borderBottom: '1px solid',
                justifyContent: 'space-between',
                borderColor: 'secondaryHighlight',
              }}
            >
              <Flex sx={{ gap: 4, alignItems: 'center' }}>
                <Button
                  variant="ghost"
                  iconLeft={<IconClose />}
                  onPress={() => {
                    if (isDirty) {
                      toggle();
                    } else {
                      window.history.length > 1
                        ? navigate(-1)
                        : navigate('/collections');
                    }
                  }}
                />
                <Box
                  sx={{
                    height: 40,
                    width: '1px',
                    backgroundColor: 'secondaryHighlight',
                  }}
                />
                <Heading variant="headingSm" sx={{ mt: 1 }}>
                  {isEditMode ? 'Edit Collection' : 'Create Collection'}
                </Heading>
              </Flex>
              <Flex sx={{ gap: 3, alignItems: 'center' }}>
                {isEditMode && (
                  <Button variant="ghost" onPress={auditModal.toggle}>
                    View History
                  </Button>
                )}
                <Button
                  variant="primary"
                  isDisabled={saving}
                  onPress={() => handleSubmit()}
                >
                  {saving
                    ? 'Saving...'
                    : isEditMode
                    ? 'Save Collection'
                    : 'Create Collection'}
                </Button>
              </Flex>
            </Flex>
            <Flex
              id="content"
              sx={{
                p: 6,
                gap: 6,
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                flexDirection: 'column',
              }}
            >
              <div id="title">
                <Heading variant="headingMd">Primary Information</Heading>
                Enter name, description, information about your collection
              </div>
              <Flex id="inputs" sx={{ gap: 4, flexDirection: 'column' }}>
                <Grid sx={{ gap: 3, gridTemplateColumns: ['1fr', '1fr 1fr'] }}>
                  <Field name="name" validate={required}>
                    {({ input, meta }) => {
                      const error =
                        (meta.touched && meta.error) || meta.submitError;
                      return (
                        <Box>
                          <Heading variant="headingXs">Title</Heading>
                          <Input
                            {...input}
                            disabled={saving}
                            placeholder="Enter collection title"
                            onBlur={(event: FocusEvent<HTMLInputElement>) => {
                              input.onBlur(event);
                              const currentSlug = form.getState().values.slug;
                              if (!currentSlug) {
                                const slug = event.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z\s-]/g, '')
                                  .trim()
                                  .replace(/\s+/g, '-')
                                  .replace(/-+/g, '-');
                                form.change('slug', slug);
                              }
                            }}
                            sx={{
                              borderColor: error ? 'errorDefault' : undefined,
                            }}
                          />
                          <ErrorLabel message={error} />
                        </Box>
                      );
                    }}
                  </Field>
                  <Field name="slug" validate={required}>
                    {({ input, meta }) => {
                      const error =
                        (meta.touched && meta.error) || meta.submitError;
                      return (
                        <Box>
                          <Heading variant="headingXs">URL Slug</Heading>
                          <Input
                            {...input}
                            disabled={saving}
                            placeholder="enter-slug-here"
                            sx={{
                              borderColor: error ? 'errorDefault' : undefined,
                            }}
                          />
                          <ErrorLabel message={error} />
                        </Box>
                      );
                    }}
                  </Field>
                </Grid>
                <Grid sx={{ gap: 3, gridTemplateColumns: ['1fr', '1fr 1fr'] }}>
                  <Field name="description" validate={validateDescription}>
                    {({ input, meta }) => {
                      const wordCount = countWords(input.value);
                      const overLimit = wordCount > DESCRIPTION_WORD_LIMIT;
                      const error =
                        (meta.touched && meta.error) ||
                        meta.submitError ||
                        (overLimit ? meta.error : undefined);
                      return (
                        <Box>
                          <Heading variant="headingXs">Description</Heading>
                          <Textarea
                            {...input}
                            variant="forms.inputs"
                            placeholder="Enter description"
                            rows={2}
                            disabled={saving}
                            sx={{
                              height: 'auto',
                              resize: 'none',
                              minHeight: 'unset',
                              borderColor: error ? 'errorDefault' : undefined,
                            }}
                            onChange={(
                              event: ChangeEvent<HTMLTextAreaElement>,
                            ) => {
                              const value = event.target.value;
                              const trailingSpace = /\s$/.test(value);
                              const words = value.split(/\s+/).filter(Boolean);
                              if (words.length > DESCRIPTION_WORD_LIMIT) {
                                input.onChange(
                                  words
                                    .slice(0, DESCRIPTION_WORD_LIMIT)
                                    .join(' '),
                                );
                              } else if (
                                words.length === DESCRIPTION_WORD_LIMIT &&
                                trailingSpace
                              ) {
                                input.onChange(words.join(' '));
                              } else {
                                input.onChange(event);
                              }
                            }}
                          />
                          <Flex sx={{ justifyContent: 'space-between' }}>
                            <ErrorLabel message={error} />
                            <Label
                              sx={{
                                pt: 1,
                                color: overLimit
                                  ? 'errorDefault'
                                  : 'secondaryText',
                              }}
                            >
                              {`${wordCount}/${DESCRIPTION_WORD_LIMIT} words`}
                            </Label>
                          </Flex>
                        </Box>
                      );
                    }}
                  </Field>
                  <Box>
                    <Heading variant="headingXs">
                      Markets with available items
                    </Heading>
                    <Grid
                      sx={{
                        mt: 2,
                        gap: 2,
                        gridTemplateColumns: [
                          '1fr',
                          'repeat(2, 1fr)',
                          'repeat(3, 1fr)',
                        ],
                      }}
                    >
                      {warehouses.map((wh) => {
                        const count = itemWarehouseCounts.get(wh.name) ?? 0;
                        return (
                          <Label
                            key={wh.id}
                            sx={{
                              fontSize: 1,
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              opacity: count > 0 ? 1 : 0.5,
                            }}
                          >
                            <Checkbox checked={count > 0} disabled />
                            {`${wh.name}${
                              count > 0
                                ? ` (${count} ${
                                    count === 1 ? 'Item' : 'Items'
                                  })`
                                : ''
                            }`}
                          </Label>
                        );
                      })}
                    </Grid>
                  </Box>
                </Grid>
              </Flex>
              <CreativeAsset
                title="Collection Page Banner"
                disabled={saving}
                warehouses={warehouses}
                onFileSelect={handleFileSelect}
                warehouseIds={values.warehouseIds}
                images={[
                  {
                    label: 'Desktop',
                    requiredWidth: 2288,
                    requiredHeight: 480,
                    fieldName: 'desktopHeroImage',
                    value: values.desktopHeroImage,
                  },
                  {
                    label: 'Mobile',
                    requiredWidth: 792,
                    requiredHeight: 320,
                    fieldName: 'mobileHeroImage',
                    value: values.mobileHeroImage,
                  },
                ]}
                pendingFiles={pendingFilesRef.current}
                itemWarehouseCounts={itemWarehouseCounts}
                onImageChange={(fieldName, key) => {
                  form.change(fieldName as keyof CollectionFormData, key);
                }}
                onWarehouseIdsChange={(ids) => form.change('warehouseIds', ids)}
              />
              <CollectionItemsTable
                items={items}
                disabled={saving}
                onImport={(ids) => {
                  setItemIds(ids);
                  form.change('itemIds', ids);
                }}
              />
              {isEditMode && canDeleteCollection && (
                <Flex
                  sx={{
                    pt: 4,
                    justifyContent: 'flex-end',
                    borderTop: '1px solid',
                    borderColor: 'secondaryHighlight',
                  }}
                >
                  <Button
                    variant="slay"
                    onPress={() => setDeleteModalVisible(true)}
                  >
                    Delete Collection
                  </Button>
                </Flex>
              )}
            </Flex>
          </Flex>
          <UnsavedChangesModal
            visible={visible}
            toggle={toggle}
            handleClose={() =>
              window.history.length > 1
                ? navigate(-1)
                : navigate('/collections')
            }
          />
          {isEditMode && canDeleteCollection && (
            <DeleteCollectionModal
              visible={deleteModalVisible}
              collection={
                collectionData?.collectionSetupForEdit?.collection
                  ? {
                      id: id!,
                      name:
                        collectionData.collectionSetupForEdit.collection.name ??
                        '',
                    }
                  : undefined
              }
              close={() => setDeleteModalVisible(false)}
              submit={() => navigate('/collections')}
            />
          )}
          {isEditMode && (
            <CollectionAuditLogPane
              collectionId={id}
              onClose={auditModal.toggle}
              visible={auditModal.visible}
            />
          )}
        </>
      )}
    />
  );
}
